/**
 * How Activity writes a drive, a text's length, an email and a moment —
 * `docs/activity.md` §3 (Drive notes, Units), §7.5, §7.6.
 *
 * Formatting only. Every number here was worked out by the server (rule 2);
 * these turn it into the boards' words on both sides.
 */
import { minutesWords } from '../requests/words.js';
import { QUOTE_LINES, htmlToText } from './email-html.js';
/** A drive note's tone: amber under 5 min spare, red when it doesn't fit (§6.3). */
export function driveTone(spare) {
    if (spare === null)
        return 'ok';
    if (spare < 0)
        return 'short';
    if (spare < 5)
        return 'tight';
    return 'ok';
}
/** *8 min* · *14 min · 1 spare* · *14 min · 3 short*. */
export function driveNote(minutes, spare) {
    const tone = driveTone(spare);
    if (tone === 'tight')
        return `${minutes} min · ${spare} spare`;
    if (tone === 'short')
        return `${minutes} min · ${-spare} short`;
    return `${minutes} min`;
}
/** The tooltip on every calendar drive: they are all estimates (D7). */
export const driveTooltip = (minutes) => `About ${minutes} min, estimated from the distance`;
/** *52 min driving*, *1 h 6 min driving* — a person's day (Board 4). */
export const drivingWords = (minutes) => `${minutesWords(minutes)} driving`;
/** *about 52 min driving* — the phone's day line, which says it is an estimate. */
export const aboutDrivingWords = (minutes) => `about ${minutesWords(minutes)} driving`;
// ---------------------------------------------------------------------------
// Texts (§7.5)
// ---------------------------------------------------------------------------
/**
 * Smart punctuation made plain before counting and sending, so ordinary
 * writing stays GSM-7: *—* → *-*, curly quotes → straight, *…* → *...*.
 */
export function plainPunctuation(text) {
    return text
        .replace(/[–—]/g, '-')
        .replace(/[‘’‚′]/g, "'")
        .replace(/[“”„″]/g, '"')
        .replace(/…/g, '...')
        .replace(/ /g, ' ');
}
const GSM_BASIC = new Set('@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà');
const GSM_EXTENDED = new Set('^{}\\[~]|€\f');
/**
 * How many texts a message is (§7.5): GSM-7 is 160, then 153 a part; any
 * other character — an emoji — makes it UCS-2, 70 then 67.
 */
export function countText(raw) {
    const text = plainPunctuation(raw);
    let gsm = true;
    let used = 0;
    for (const char of text) {
        if (GSM_BASIC.has(char))
            used += 1;
        else if (GSM_EXTENDED.has(char))
            used += 2;
        else {
            gsm = false;
            break;
        }
    }
    if (!gsm) {
        /* UCS-2 counts code units: an emoji outside the BMP is two. */
        used = text.length;
        const parts = used <= 70 ? 1 : Math.ceil(used / 67);
        return { parts, used, limit: 70, gsm: false };
    }
    const parts = used <= 160 ? 1 : Math.ceil(used / 153);
    return { parts, used, limit: 160, gsm: true };
}
/** *1 text · 104 / 160*, *2 texts* — the composer's counter (Board 11 B). */
export function textCountWords(count) {
    if (count.parts > 1)
        return `${count.parts} texts`;
    return `1 text · ${count.used} / ${count.limit}`;
}
// ---------------------------------------------------------------------------
// Email (§7.6)
// ---------------------------------------------------------------------------
/** A person's text as the HTML `drainCommunications` sends: escaped, line breaks as `<br>`. */
export function textToHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/\r\n|\r|\n/g, '<br>');
}
/**
 * An email body as text (§19 B14): HTML converted, and the quoted history
 * below the first *On … wrote:* or *From:* line dropped.
 */
export function emailToText(body, isHtml) {
    /* `htmlToText` (email-html.ts): comments and the head go, entities are read, blocks are lines. */
    const text = isHtml ? htmlToText(body) : body;
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    /* The same lines the thread folds an HTML email's history at (`QUOTE_LINES`). */
    const cut = lines.findIndex((line) => QUOTE_LINES.some((pattern) => pattern.test(line)));
    const kept = cut >= 0 ? lines.slice(0, cut) : lines;
    return kept.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}
/** A preview line: the first 200 characters, on one line. */
export function previewOf(text) {
    return text.replace(/\s+/g, ' ').trim().slice(0, 200);
}
/** *Re: Front walkway quote*, once. */
export function replySubject(subject) {
    const base = (subject ?? '').trim();
    if (!base)
        return '';
    return /^re:/i.test(base) ? base : `Re: ${base}`;
}
// ---------------------------------------------------------------------------
// When (§3 Units)
// ---------------------------------------------------------------------------
/**
 * *9:32 AM* today, *Yesterday*, *Mon* within the week, *Oct 12* before that —
 * a conversation row's time (§7.1). `local` is the moment's day and minutes in
 * the viewer's zone; `today` the viewer's day.
 */
export function listTimeWords(local, today, dayWords) {
    if (local.date === today)
        return dayWords.clock(local.minutes);
    if (local.date === dayWords.addDays(today, -1))
        return 'Yesterday';
    if (local.date > dayWords.addDays(today, -7))
        return dayWords.weekdayShort(local.date);
    return dayWords.monthDay(local.date);
}
/**
 * *now*, *this morning*, *Tuesday*, *Oct 12* — when a device was last used
 * (Board 17 F).
 */
export function lastUsedWords(local, now, words) {
    if (local.date === now.date) {
        if (now.minutes - local.minutes < 10)
            return 'now';
        if (local.minutes < 12 * 60)
            return 'this morning';
        if (local.minutes < 17 * 60)
            return 'this afternoon';
        return 'this evening';
    }
    if (local.date === words.addDays(now.date, -1))
        return 'yesterday';
    if (local.date > words.addDays(now.date, -7))
        return words.weekdayLong(local.date);
    return words.monthDay(local.date);
}
/** *2 days late*, *1 day late*. */
export const daysLateWords = (days) => (days === 1 ? '1 day late' : `${days} days late`);
/** *Now · 4 min left* (Board 13 A). */
export const minutesLeftWords = (minutes) => `Now · ${minutes} min left`;
//# sourceMappingURL=words.js.map